/**
 * Single source of truth for the catalogue.
 *
 * Consumed by pages/Products.js, pages/SearchResults.js and
 * components/ProductModal.js. `keywords` is the search surface — search matches
 * name, description, formula, applications and keywords.
 *
 * `slug` is derived below and is the product's public URL segment
 * (/products/<slug>), so it is also what data/seoData.js keys social images on.
 */

const catalogue = [
  {
    id: 1,
    name: "Potassium Bicarbonate",
    grade: "Technical Grade",
    formula: "KHCO₃",
    purity: "99.5%",
    packaging: "25 kg HDPE bag",
    appearance: "White powder",
    category: "Carbonates",
    description:
      "Technical Grade, 25kg HDPE Bag. White powder, high purity (99.5%) industrial potassium bicarbonate (KHCO3). Used as leavening agent, fire extinguishing agent, and in pharmaceutical and industrial processes.",
    applications: ["Leavening agent", "Dry-chemical fire suppression", "Pharmaceutical processing", "pH buffering"],
    specs: [
      { label: "Purity", value: "99.5% min" },
      { label: "Molar mass", value: "100.12 g/mol" },
      { label: "Packing", value: "25 kg HDPE bag" },
      { label: "Grade", value: "Technical" },
    ],
    keywords: [
      "potassium",
      "bicarbonate",
      "KHCO3",
      "technical",
      "industrial",
      "leavening",
      "fire extinguisher",
      "carbonates",
      "salts",
    ],
    image: "/images/Potassium Bicarbonate Technical Grade.webp",
  },
  {
    id: 2,
    name: "Potassium Carbonate",
    grade: "Industrial Grade",
    formula: "K₂CO₃",
    purity: "99%",
    packaging: "25 kg HDPE bag",
    appearance: "White granules",
    category: "Carbonates",
    description:
      "Industrial Grade, 25kg HDPE Bag. White granules, high purity (99%) potassium carbonate (K2CO3) for industrial use. Used in glass making, fertilizers, and chemical industry.",
    applications: ["Glass manufacturing", "Fertilizer blending", "Chemical synthesis", "Soap & detergents"],
    specs: [
      { label: "Purity", value: "99% min" },
      { label: "Molar mass", value: "138.21 g/mol" },
      { label: "Packing", value: "25 kg HDPE bag" },
      { label: "Grade", value: "Industrial" },
    ],
    keywords: [
      "potassium",
      "carbonate",
      "K2CO3",
      "industrial",
      "glass",
      "fertilizer",
      "carbonates",
      "salts",
    ],
    image: "/images/Potassium Carbonate Technical Grade.webp",
  },
  {
    id: 3,
    name: "Ammonium Molybdate",
    grade: "Technical Grade",
    formula: "(NH₄)₂MoO₄",
    purity: "98%",
    packaging: "25 kg HDPE bag",
    appearance: "White powder",
    category: "Molybdates",
    description:
      "98% pure ammonium molybdate (NH4)2MoO4, 25kg HDPE Bag. Used in industrial processes. White powder with molar mass 196.02 g/mol and melting point 81°C.",
    applications: ["Catalyst manufacture", "Micronutrient fertilizer", "Analytical reagent", "Corrosion inhibition"],
    specs: [
      { label: "Purity", value: "98% min" },
      { label: "Molar mass", value: "196.02 g/mol" },
      { label: "Melting point", value: "81 °C" },
      { label: "Packing", value: "25 kg HDPE bag" },
    ],
    keywords: [
      "ammonium",
      "molybdate",
      "NH4",
      "MoO4",
      "industrial",
      "powder",
      "molybdates",
      "salts",
      "catalyst",
    ],
    image: "/images/Ammonium Molybdate Powder.webp",
  },
  {
    id: 4,
    name: "Magnesium Oxide",
    grade: "Technical Grade",
    formula: "MgO",
    purity: "85%",
    packaging: "50 kg HDPE bag",
    appearance: "White odourless powder",
    category: "Oxides",
    description:
      "85% pure magnesium oxide (MgO), 50kg HDPE Bag. White, odorless, tasteless powder for industrial applications. Net weight 50 kg. Shelf life 1 year.",
    applications: ["Refractory linings", "Animal feed supplement", "Rubber & polymer filler", "Soil magnesium source"],
    specs: [
      { label: "Purity", value: "85% min" },
      { label: "Net weight", value: "50 kg" },
      { label: "Shelf life", value: "12 months" },
      { label: "Packing", value: "50 kg HDPE bag" },
    ],
    keywords: [
      "magnesium",
      "oxide",
      "MgO",
      "industrial",
      "powder",
      "compound",
      "oxides",
      "refractory",
      "feed",
    ],
    image: "/images/Magnesium Oxide Powder.webp",
  },
  {
    id: 5,
    name: "Sodium Molybdate",
    grade: "Technical Grade",
    formula: "Na₂MoO₄",
    purity: "99.5%",
    packaging: "25 kg HDPE bag",
    appearance: "White powder",
    category: "Molybdates",
    description:
      "99.5% pure sodium molybdate (Na2MoO4), 25kg HDPE Bag. White powder used in industrial applications. Molar mass 205.92 g/mol. Shelf life 1 year.",
    applications: ["Corrosion inhibitor", "Micronutrient fertilizer", "Pigment production", "Catalyst manufacture"],
    specs: [
      { label: "Purity", value: "99.5% min" },
      { label: "Molar mass", value: "205.92 g/mol" },
      { label: "Shelf life", value: "12 months" },
      { label: "Packing", value: "25 kg HDPE bag" },
    ],
    keywords: [
      "sodium",
      "molybdate",
      "Na2MoO4",
      "industrial",
      "powder",
      "compound",
      "molybdates",
      "salts",
      "corrosion",
    ],
    image: "/images/Sodium Molybdate Powder.webp",
  },
  {
    id: 6,
    name: "Ammonium Chloride",
    grade: "Technical Grade",
    formula: "NH₄Cl",
    purity: "99%",
    packaging: "25 kg bag",
    appearance: "White crystalline powder",
    category: "Ammonium Salts",
    description:
      "Technical Grade, 25kg Bag. White crystalline powder (NH4Cl), purity 99%, molecular weight 53.49 g/mol. Used in food industry, fertilizers, and various industrial applications.",
    applications: ["Food industry", "Nitrogen fertilizer", "Electroplating & flux", "Metal finishing"],
    specs: [
      { label: "Purity", value: "99% min" },
      { label: "Molar mass", value: "53.49 g/mol" },
      { label: "Packing", value: "25 kg bag" },
      { label: "Grade", value: "Technical" },
    ],
    keywords: [
      "ammonium",
      "chloride",
      "NH4Cl",
      "food industry",
      "technical",
      "fertilizer",
      "industrial",
      "salts",
    ],
    image: "/images/Ammonium Chloride Powder.webp",
  },
  {
    id: 7,
    name: "Phosphorous Acid",
    grade: "Technical Grade",
    formula: "H₃PO₃",
    purity: "98.5%",
    packaging: "25 kg bag",
    appearance: "White crystalline powder",
    category: "Acids & Intermediates",
    description:
      "Phosphorous Acid (H3PO3), white crystalline powder. Used as an intermediate in chemical synthesis, for production of phosphonates, and as a reducing agent in industrial applications.",
    applications: ["Phosphonate production", "Chemical intermediate", "Reducing agent", "Water treatment"],
    specs: [
      { label: "Assay", value: "98.5% min" },
      { label: "Molar mass", value: "81.99 g/mol" },
      { label: "Form", value: "Crystalline powder" },
      { label: "Packing", value: "25 kg bag" },
    ],
    keywords: [
      "phosphorous",
      "acid",
      "H3PO3",
      "intermediate",
      "phosphonates",
      "reducing agent",
      "industrial",
      "compound",
      "acids",
    ],
    image: "/images/Phosphoric Acid.webp",
  },
  {
    id: 8,
    name: "Potassium Humate Flakes",
    grade: "Organic",
    formula: "Humate",
    purity: "98% soluble",
    packaging: "25 kg bag",
    appearance: "Black shiny flakes",
    category: "Humic & Organic",
    description:
      "Organic Potassium Humate Flakes, 98% soluble, black shiny flakes. 25kg bags. Enhances soil fertility and plant growth. Used in agriculture as a soil conditioner and fertilizer enhancer.",
    applications: ["Soil conditioning", "Fertilizer enhancement", "Root development", "Crop yield improvement"],
    specs: [
      { label: "Solubility", value: "98%" },
      { label: "Form", value: "Shiny flakes" },
      { label: "Origin", value: "Organic" },
      { label: "Packing", value: "25 kg bag" },
    ],
    keywords: [
      "potassium",
      "humate",
      "flakes",
      "organic",
      "fertilizer",
      "agriculture",
      "soil enhancer",
      "humic",
    ],
    image: "/images/potassium-humate-flakes.jpeg",
  },
  {
    id: 9,
    name: "Potassium Humate Powder",
    grade: "Organic",
    formula: "Humate",
    purity: "100% soluble",
    packaging: "25 kg bag",
    appearance: "Fine black powder",
    category: "Humic & Organic",
    description:
      "Fine black powder, 100% soluble Potassium Humate. Supplied in 25kg bags. Used in soil conditioning, plant nutrition, and crop improvement in agriculture.",
    applications: ["Soil conditioning", "Plant nutrition", "Drip & foliar feeding", "Crop improvement"],
    specs: [
      { label: "Solubility", value: "100%" },
      { label: "Form", value: "Fine powder" },
      { label: "Origin", value: "Organic" },
      { label: "Packing", value: "25 kg bag" },
    ],
    keywords: [
      "potassium",
      "humate",
      "powder",
      "agriculture",
      "plant nutrition",
      "fertilizer",
      "soil",
      "humic",
      "organic",
    ],
    image: "/images/Potassium Humate Powder.webp",
  },
  {
    id: 10,
    name: "Sodium Humate Flakes",
    grade: "Organic",
    formula: "Humate",
    purity: "Water soluble",
    packaging: "25 kg bag",
    appearance: "Dark brown to black flakes",
    category: "Humic & Organic",
    description:
      "Sodium Humate Flakes, dark brown to black, water-soluble, supplied in 25kg bags. Acts as a chelating agent and improves nutrient uptake. Widely used in animal feed and agriculture.",
    applications: ["Animal feed additive", "Chelating agent", "Nutrient uptake", "Soil amendment"],
    specs: [
      { label: "Solubility", value: "Water soluble" },
      { label: "Colour", value: "Dark brown to black" },
      { label: "Origin", value: "Organic" },
      { label: "Packing", value: "25 kg bag" },
    ],
    keywords: [
      "sodium",
      "humate",
      "flakes",
      "animal feed",
      "fertilizer",
      "chelating",
      "agriculture",
      "humic",
      "organic",
    ],
    image: "/images/Sodium Humate Flakes.jpg",
  },
  {
    id: 11,
    name: "Potassium Fulvate",
    grade: "Organic",
    formula: "Fulvate",
    purity: "Water soluble",
    packaging: "25 kg bag",
    appearance: "Fine soluble powder",
    category: "Humic & Organic",
    description:
      "Potassium Fulvate is a water-soluble organic potassium compound. Supplied in 25kg bags. Promotes plant metabolism, enhances nutrient absorption, and improves crop yield.",
    applications: ["Plant metabolism", "Nutrient absorption", "Foliar spray", "Yield improvement"],
    specs: [
      { label: "Solubility", value: "Water soluble" },
      { label: "Form", value: "Fine powder" },
      { label: "Origin", value: "Organic" },
      { label: "Packing", value: "25 kg bag" },
    ],
    keywords: [
      "potassium",
      "fulvate",
      "organic",
      "fertilizer",
      "plant growth",
      "soil conditioner",
      "agriculture",
      "humic",
    ],
    image: "/images/Potassium Fulvate Powder.webp",
  },
];

/** "Potassium Humate Flakes" -> "potassium-humate-flakes" */
const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Slugs are derived, not hand-written, so a product can never ship with a
 * URL that disagrees with its name. Every product name in the catalogue is
 * unique, which keeps the slugs unique too.
 */
export const productsData = catalogue.map((product) => ({
  ...product,
  slug: slugify(product.name),
}));

/** Canonical, indexable path for a product detail page. */
export const productPath = (product) => `/products/${product.slug}`;

export const getProductBySlug = (slug) =>
  productsData.find((p) => p.slug === slug) || null;

export const getProductById = (id) =>
  productsData.find((p) => p.id === Number(id)) || null;

/** Ordered, de-duplicated list of catalogue categories for filter chips. */
export const productCategories = productsData.reduce((acc, p) => {
  if (!acc.includes(p.category)) acc.push(p.category);
  return acc;
}, []);

/** Shared matcher so /products filtering and /search stay in sync. */
export function matchesQuery(product, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    product.name,
    product.grade,
    product.formula,
    product.category,
    product.description,
    ...(product.applications || []),
    ...(product.keywords || []),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

export function searchProducts(query) {
  return productsData.filter((p) => matchesQuery(p, query));
}
